import { ApexOptions } from 'apexcharts';
import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash/cloneDeep';
import lowerCase from 'lodash/lowerCase';
import merge from 'lodash/merge';
import { ComponentType, Suspense, lazy, useMemo } from 'react';
import type { Props as ReactApexChartProps } from 'react-apexcharts';
import { renderToString } from 'react-dom/server';
import { Spinner } from 'ui';
import { goodFormatUSDCurrencyLowPrecision } from 'ui/utils';

const Chart = lazy(() =>
  import('react-apexcharts').then(module => ({
    default: ((module as any).default?.default ?? (module as any).default ?? module) as any,
  })),
) as unknown as ComponentType<ReactApexChartProps>;

const colors = ['#0369A1', '#0EA5E9', '#737373'];

const baseOptions: ApexOptions = {
  grid: {
    show: false,
    padding: {
      left: -17,
      right: 0,
      top: 0,
      bottom: 0,
    },
  },
  chart: {
    type: 'bar',
    stacked: true,
    stackType: '100%',
    width: '100%',
    toolbar: { show: false },
  },
  dataLabels: { enabled: false },
  plotOptions: {
    bar: {
      distributed: false,
      barHeight: '100%',
      horizontal: true,
    },
  },
  stroke: {
    width: 2,
    colors: ['#fff'],
  },
  xaxis: {
    labels: { show: false },
    axisTicks: { show: false },
    axisBorder: { show: false },
  },
  yaxis: {
    labels: { show: false },
    axisBorder: { show: false },
  },
  fill: { opacity: 1 },
  legend: { show: false },
  tooltip: {
    followCursor: true,
    custom: ({ seriesIndex, series, w }) => {
      const label = capitalize(lowerCase(w.config.series[seriesIndex]?.name || ''));
      return renderToString(
        <div className="p-2">
          {label}: {goodFormatUSDCurrencyLowPrecision(series[seriesIndex])}
        </div>,
      );
    },
  },
};

export interface BalanceBarChartProps {
  options?: ApexOptions;
  data: Record<string, { total: number; percentage: number } | undefined>;
  formatTotal?: (total: number) => string;
}

const BalanceBarChart = ({ data, options = {}, formatTotal }: BalanceBarChartProps) => {
  const { percents, labels, totals } = useMemo(
    () =>
      Object.entries(data || {}).reduce(
        (acc, [label, value]) => {
          return {
            percents: [...acc.percents, value?.percentage || 0],
            totals: [...acc.totals, value?.total || 0],
            labels: [...acc.labels, label],
          };
        },
        { percents: [] as number[], labels: [] as string[], totals: [] as number[] },
      ),
    [data],
  );

  const series = useMemo<ApexOptions['series']>(
    () =>
      totals.map((data, index) => ({ name: labels[index], data: [data], color: colors[index] })),
    [labels, totals],
  );

  const resolveOptions = useMemo(() => merge(cloneDeep(baseOptions), options), [options]);

  return (
    <Suspense
      fallback={
        <div className="min-h-[250px] h-full w-full flex justify-center items-center">
          <Spinner size="lg" />
        </div>
      }
    >
      <div className="w-full flex flex-col gap-3">
        <div>
          <div className="flex justify-between items-center mb-1">
            {labels.map((label, index) => (
              <span key={label + index} className="capitalize text-xs text-gray-500">
                {capitalize(lowerCase(label))}
              </span>
            ))}
          </div>
          <div className="flex justify-between items-center">
            {totals.map((total, index) => (
              <span className="text-sm" style={{ color: colors[index] }} key={`${total}_${index}`}>
                {formatTotal ? formatTotal(total) : total}
              </span>
            ))}
          </div>
        </div>
        <div className="h-[15px] [&>*]:relative [&>*]:top-[-29px]">
          <Chart
            type="bar"
            options={resolveOptions}
            series={series}
            width="100%"
            height={60}
            key={JSON.stringify(series)}
          />
        </div>
        <div className="flex justify-between items-center">
          {percents.map((percent, index) => (
            <span className="text-sm" style={{ color: colors[index] }} key={`${percent}_${index}`}>
              {percent.toFixed(2)}%
            </span>
          ))}
        </div>
      </div>
    </Suspense>
  );
};

export default BalanceBarChart;
